import { RangeInput } from '@/components/RangeInput';
import { Form, Slider } from 'antd';
import { useMemo } from 'react';

interface RangeSliderProps {
  name: string;
  min: number;
  max: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  formatter?: (
    value: number | undefined,
    info?: { userTyping: boolean; input: string },
  ) => string;
  parser?: (displayValue: string | undefined) => number;
  className?: string;
}

export function RangeSlider({
  name,
  min,
  max,
  step = 1,
  prefix,
  suffix,
  formatter,
  parser,
}: RangeSliderProps) {
  const marks = useMemo(() => {
    const result: Record<number, string> = {
      [min]: formatter
        ? formatter(min)
        : `${prefix || ''}${min}${suffix || ''}`,
      [max]: formatter
        ? formatter(max)
        : `${prefix || ''}${max}${suffix || ''}`,
    };
    return result;
  }, [min, max, prefix, suffix, formatter]);

  return (
    <div className="space-y-2">
      <Form.Item
        name={name}
        className="flex-1 mb-0"
        getValueFromEvent={(values: [number, number]) => ({
          min: values[0] === min ? undefined : values[0],
          max: values[1] === max ? undefined : values[1],
        })}
        getValueProps={(value: { min?: number; max?: number }) => ({
          value: [value?.min ?? min, value?.max ?? max],
        })}
      >
        <Slider
          range
          min={min}
          max={max}
          step={step}
          marks={marks}
          tooltip={{
            formatter: (value) => {
              if (value == null) return `${prefix || ''}0${suffix || ''}`;
              return formatter
                ? formatter(value)
                : `${prefix || ''}${value}${suffix || ''}`;
            },
          }}
        />
      </Form.Item>
      <Form.Item name={name} className="mb-0">
        <RangeInput
          min={min}
          max={max}
          step={step}
          prefix={prefix}
          suffix={suffix}
          formatter={formatter}
          parser={parser}
          size="small"
          className="justify-end"
        />
      </Form.Item>
    </div>
  );
}
